import axios from 'axios';
import createAuth0Client from '@auth0/auth0-spa-js';
import auth0 from 'auth0-js';
import Cookies from 'universal-cookie';
import sha256 from 'sha256';

import history from '../utils/history';
import { SESSION_STORAGE_KEYS } from '../../constants';
import { getUserFromCache, setUserToCache } from './userCache';
import { pushUserDataToDataLayer } from '../utils/pushUserDataToDataLayer';
import { identifyAndSetBrazeUser, logBrazeSignedIn, logBrazeAccountRegistration, setBrazeAuthMethod, getAuthMethodFromSub, resolveAndSyncBrazeUserLocation } from './braze';

export let client;

export const authInitCommand = async ({ endpoints, options }) => {
  const cookies = new Cookies();
  var token = cookies.get('auth0_token', { path: '/' });
  let isNewSignIn = false; // Track whether this is a fresh sign-in vs existing session

  client = await createAuth0Client({
    ...options,
    ui_locales: window.freedomeConfig.locale,
  });

  if (
    !token &&
    window.location.search.includes('code=') &&
    window.location.search.includes('state=')
  ) {
    const { appState } = await client.handleRedirectCallback();
    // console.log(
    //   '[freedome][auth] Handling login request, redirecting user to target url...'
    // );
    history.replace(
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname
    );

    return { isAuthenticated: false };
  }

  // without this check we have a blank page
  if (
    token &&
    window.location.search.includes('code=') &&
    window.location.search.includes('state=')
  ) {
    window.location.href = window.location.href.split('?')[0];
  }

  if (
    !token &&
    window.location.hash.includes('access_token=') &&
    window.location.hash.includes('state=')
  ) {
    // console.log(
    //   '[freedome][auth] Handling login request, redirecting user to target url...'
    // );

    var webAuth = new auth0.WebAuth({
      domain: process.env.REACT_APP_AUTH0_DOMAIN,
      clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
    });

    webAuth.parseHash(
      { hash: window.location.hash },
      function (err, authResult) {
        if (err) {
          return console.log(err);
        }

        token = authResult.accessToken;
        isNewSignIn = true;
        // console.log(`[freedome][auth] User authenticated via SDK`);

        // TODO: add cookie parames, the code below seems not working
        // expires: new Date(Date.now() + (authResult.expiresIn * 1000)), httpOnly: true,
        cookies.set('auth0_token', token, { path: '/' });
      }
    );
  }

  if (!token) {
    const auth0User = await client.getUser();
    const isAuthenticated = !!auth0User;

    if (!isAuthenticated) {
      // user is not authenticated
      // console.log('[freedome][auth] User is not authenticated');
      return { isAuthenticated };
    }

    token = await client.getTokenSilently();
    isNewSignIn = true;
    // TODO: add cookie parames, the code below seems not working
    // expires: new Date(Date.now() + (authResult.expiresIn * 1000)), httpOnly: true,
    cookies.set('auth0_token', token, { path: '/', sameSite: true });
    // console.log(
    //   `[freedome][auth] User authenticated via Universal Login, subject claim is ${auth0User.sub}`
    // );
  }

  if (token) {
    // console.log(`[freedome][auth] Token loaded successfully`);

    let user = getUserFromCache();
    if (!user) {
      const res = await axios.get(`${endpoints.core}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { locale: window.freedomeConfig.locale },
      });

      if (res.status !== 200) {
        throw new Error('Invalid response from core');
      }

      user = res.data;
      setUserToCache(user);
    } else {
      // console.log(`[freedome][auth] Using cached user data`);
    }
    const role = user.role;

    // console.log(`[freedome][auth] User data loaded successfully`);

    pushUserDataToDataLayer(user, 'userLoggedIn');

    // Identify user in PostHog. authInitCommand runs on every page load, so we
    // skip identify() when PostHog already has this distinct_id — otherwise each
    // call emits a $set event for person properties that haven't changed.
    if (window.posthog && typeof window.posthog.identify === 'function') {
      const currentDistinctId =
        typeof window.posthog.get_distinct_id === 'function'
          ? window.posthog.get_distinct_id()
          : null;
      if (currentDistinctId !== user.id) {
        window.posthog.identify(user.id, { email: user.email });
      }
    }

    // Identify user in Braze and set attributes
    identifyAndSetBrazeUser(user);

    // Resolve and sync the user's location to Braze. Gated on authentication
    // (only identified users) and cached for 30 days per user to minimise
    // Google Geolocation / Geocoding API costs.
    resolveAndSyncBrazeUserLocation(user);

    // Only log signed_in event on actual sign-in, not on every page load
    if (isNewSignIn) {
      logBrazeSignedIn();
    }

    // Set auth_method derived from user.sub
    const authMethod = getAuthMethodFromSub(user.sub);
    if (authMethod) {
      setBrazeAuthMethod(authMethod);
    }

    // Detect first-ever login via social auth: if account was created within the last
    // 2 minutes and method is NOT email_password (those are already handled in the
    // signup callbacks), fire account_registration_completed for Google/Facebook/Apple.
    if (isNewSignIn && user.created_at && authMethod && authMethod !== 'email_password') {
      const createdAt = new Date(user.created_at);
      const now = new Date();
      const twoMinutesMs = 2 * 60 * 1000;
      if (now - createdAt < twoMinutesMs) {
        const referrer = window.location.pathname.includes('/checkout') ? 'checkout' : 'menu';
        logBrazeAccountRegistration(authMethod, referrer);
      }
    }

    return { isAuthenticated: true, role, token, user };
  }

  return { isAuthenticated: false };
};

export const authDoPendingActionsCommand = (dispatch, isAuthenticated) => {
  if (isAuthenticated) {
    const pendingActions = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEYS.PENDING_ACTIONS) || '[]'
    );
    pendingActions.forEach((pendingAction) => {
      dispatch(pendingAction);
    });
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.PENDING_ACTIONS);
  }
};
